import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSEO } from '../lib/seo';
import { LEGAL_PAGES, slugify } from '../content/legal';
import { SITE } from '../lib/site';
import { Reveal } from '../components/Reveal';

const Block = ({ block }) => {
  switch (block.t) {
    case 'ul':
      return (
        <ul className="my-4 space-y-2.5">
          {block.c.map((item) => (
            <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-brand-body">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sky" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'h4':
      return <h4 className="mt-8 text-base font-extrabold text-brand-navy">{block.c}</h4>;
    case 'b':
      return <p className="my-4 text-[15px] font-bold leading-relaxed text-brand-navy">{block.c}</p>;
    case 'contact':
      return (
        <div className="my-5 rounded-2xl border border-brand-lineBlue bg-brand-iceSoft p-5 text-[15px] leading-relaxed text-brand-body" data-testid="legal-contact-card">
          <p className="font-extrabold text-brand-navy">CapableBee</p>
          <p className="mt-1.5">
            Email: <a className="font-bold text-brand-skyDeep hover:underline" href={`mailto:${SITE.email}`}>{SITE.email}</a>
          </p>
          <p>
            Website:{' '}
            <a className="font-bold text-brand-skyDeep hover:underline" href="https://capablebee.com" target="_blank" rel="noopener noreferrer">
              https://capablebee.com
            </a>
          </p>
        </div>
      );
    default:
      return <p className="my-4 text-[15px] leading-relaxed text-brand-body">{block.c}</p>;
  }
};

export const Legal = ({ page }) => {
  const doc = LEGAL_PAGES[page];
  useSEO({ title: `${doc.title} — CapableBee`, description: doc.intro.slice(0, 155) });

  return (
    <main className="pt-[72px]" data-testid={`legal-page-${page}`}>
      <header className="relative overflow-hidden bg-gradient-to-b from-brand-ice via-brand-iceSoft to-white py-16 lg:py-20">
        <div className="bg-grid mask-fade-b pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="container-x relative max-w-3xl">
          <Reveal>
            <h1 className="text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl">{doc.title}</h1>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-5 text-base leading-relaxed text-brand-body/90">{doc.intro}</p>
          </Reveal>
          {doc.intro2 && (
            <Reveal delay={0.12}>
              <p className="mt-3 text-base leading-relaxed text-brand-body/90">{doc.intro2}</p>
            </Reveal>
          )}
          <Reveal delay={0.16}>
            <p className="mt-6 inline-block rounded-full border border-brand-lineBlue bg-white px-4 py-1.5 text-xs font-bold text-brand-skyDeep shadow-card">
              {doc.effective}
            </p>
          </Reveal>
        </div>
      </header>

      <div className="container-x grid gap-12 py-14 lg:grid-cols-[1fr_230px] lg:gap-16 lg:py-20">
        <article className="mx-auto w-full max-w-3xl">
          {doc.sections.map((s) => (
            <section key={s.h} id={slugify(s.h)} className="scroll-mt-28 border-b border-slate-100 pb-8 last:border-0">
              <h2 className="text-xl font-extrabold tracking-tight text-brand-navy sm:text-2xl">{s.h}</h2>
              {s.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </section>
          ))}

          <div className="mt-10">
            <Link
              to="/"
              data-testid="legal-back-home"
              className="inline-flex items-center gap-2 text-sm font-bold text-brand-skyDeep transition-colors hover:text-brand-navy"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
          </div>
        </article>

        <nav aria-label="On this page" className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-brand-line bg-white p-5 shadow-card">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">On this page</p>
            <ul className="mt-4 space-y-2.5">
              {doc.sections.map((s) => (
                <li key={s.h}>
                  <a
                    href={`#${slugify(s.h)}`}
                    data-testid="legal-toc-link"
                    className="block text-[13px] font-semibold leading-snug text-brand-body/80 transition-colors hover:text-brand-skyDeep"
                  >
                    {s.h}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </main>
  );
};
